/**
 * WEB-17: ОДИН опрос непрочитанного на весь каркас кабинета.
 *
 * 🔴 Зачем контекст, а не второй вызов `useUnread`. Хук не «читает готовое», а
 * САМ опрашивает сервер: `/msg/members` и `/msg/rooms` раз в 30 с, плюс
 * внеочередной запрос при возврате на вкладку. Пункт «Чат» в меню и значок в
 * шапке — два разных места ОДНОГО каркаса; вызвав хук в каждом, мы получили бы
 * вдвое больше запросов и два счётчика, расходящихся между собой: ответы
 * приходят вразнобой, и меню показывало бы одно число, а шапка другое.
 * Поэтому хук зовётся ровно один раз — в `CabinetShell`, — а меню и шапка
 * читают одно и то же значение.
 *
 * 🔴 Без провайдера возвращается ПУСТОЕ состояние, а не исключение. Aside и
 * значок рендерятся в тестах поодиночке, и падение там означало бы «сломан
 * каркас», хотя сломано лишь окружение теста. Пустое состояние честно гасит
 * оба места: раздела «Чат» просто нет.
 */
import React, { createContext, useContext } from 'react';
import { useUnread } from './useUnread';

/**
 * Значение по умолчанию. Ссылка СТАБИЛЬНАЯ (объявлено один раз, а не в теле
 * функции): новый объект на каждый вызов заставлял бы перерисовываться всё,
 * что от него зависит, на каждый рендер родителя.
 */
const EMPTY = Object.freeze({
  enabled: false,
  members: [],
  room: null,
  total: 0,
  permission: 'unsupported',
  askPermission: async () => 'unsupported',
  refresh: () => {},
});

const UnreadContext = createContext(null);

export const UnreadProvider = ({ children }) => {
  const value = useUnread();
  return <UnreadContext.Provider value={value}>{children}</UnreadContext.Provider>;
};

/** Непрочитанное для тех, кто внутри каркаса. Вне его — пусто, см. докстринг. */
export function useUnreadValue() {
  return useContext(UnreadContext) ?? EMPTY;
}

export default UnreadContext;
